import React, { useState, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Category order
const categoryOrder = [
  "Mob Lynching",
  "Police Atrocity",
  "Communal Violence",
  "Demolition",
  "Orphans",
  "Refugees",
  "Hafiz",
  "Impoverish",
  "Disaster",
];

// Button styles
const buttonStyles = {
  active: {
    background: "#299d8e",
    color: "#ffffff",
    border: "2px solid #299d8e",
    padding: "0.2rem 2rem",
    fontWeight: "600",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  inactive: {
    background: "#f5f5f5",
    color: "#000000",
    border: "2px solid #E1E1DF",
    padding: "0.2rem 2rem",
    transition: "all 0.3s ease",
  },
};

const ArrowLeft = (props) => (
  <button
    {...props}
    className="category-carousel-arrow category-carousel-arrow-prev"
    aria-label="Previous"
    style={{
      position: "absolute",
      left: 0,
      top: "26%",
      transform: "translateY(-50%)",
      zIndex: 20,
      background: "#ffffff",
      border: "1.5px solid #2aa89a",
      borderRadius: "50%",
      width: 34,
      height: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "none",
      cursor: "pointer",
      color: "#2aa89a",
    }}
  >
    <FaChevronLeft size={16} />
  </button>
);

const ArrowRight = (props) => (
  <button
    {...props}
    className="category-carousel-arrow category-carousel-arrow-next"
    aria-label="Next"
    style={{
      position: "absolute",
      right: 0,
      top: "26%",
      transform: "translateY(-50%)",
      zIndex: 20,
      background: "#ffffff",
      border: "1.5px solid #2aa89a",
      borderRadius: "50%",
      width: 34,
      height: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "none",
      cursor: "pointer",
      color: "#2aa89a",
    }}
  >
    <FaChevronRight size={16} />
  </button>
);

const Category = ({
  categoryData,
  kidsData,
  onCategoryHandler,
  activeIndex,
  externalCategoriesDivRef,
  categoriesDivRef: forwardedCategoriesDivRef,
}) => {
  const [localActiveIndex, setLocalActiveIndex] = useState(-1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentSlide, setCurrentSlide] = useState(0);
  const categoriesDivLocalRef = useRef(null);
  const sliderRef = useRef(null);
  const desktopCategoriesRef =
    externalCategoriesDivRef ||
    forwardedCategoriesDivRef ||
    categoriesDivLocalRef;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== null) {
      setLocalActiveIndex(activeIndex);
    } else if (activeIndex === null) {
      setLocalActiveIndex(-1);
    }
  }, [activeIndex]);

  const sortCategoriesByOrder = (categories) => {
    if (!categories || !Array.isArray(categories)) return [];
    return [...categories].sort((a, b) => {
      const nameA = a.category_name || a.name || "";
      const nameB = b.category_name || b.name || "";
      const indexA = categoryOrder.indexOf(nameA);
      const indexB = categoryOrder.indexOf(nameB);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });
  };

  const sortedCategories = sortCategoriesByOrder(categoryData || []);

  const handleCategoryClick = (e, categoryId, index) => {
    setLocalActiveIndex(index);
    onCategoryHandler(e, categoryId, index);
  };

  const handleResetClick = (e) => {
    setLocalActiveIndex(-1);
    onCategoryHandler(e, null, -1);
  };

  const getCategoryCount = (categoryId) => {
    if (!kidsData || !Array.isArray(kidsData)) return 0;
    if (categoryId === null) return kidsData.length;
    return kidsData.filter((kid) => {
      const kidCategoryId = kid.category_id || kid.categoryId || kid.category?.id;
      return kidCategoryId === categoryId;
    }).length;
  };

  const scrollDesktopCategories = (direction) => {
    if (!desktopCategoriesRef.current) return;
    desktopCategoriesRef.current.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  };

  const isMobile = windowWidth < 1024;
  const showMobileArrows = windowWidth < 768;
  const slidesToShow = windowWidth < 480 ? 2 : windowWidth < 768 ? 3 : 4;
  const totalSlides = sortedCategories.length + 1;

  const settings = {
    arrows: windowWidth >= 768,
    prevArrow: <ArrowLeft />,
    nextArrow: <ArrowRight />,
    slidesToShow,
    slidesToScroll: 1,
    centerMode: false,
    centerPadding: "0",
    infinite: false,
    adaptiveHeight: true,
    swipeToSlide: true,
    touchThreshold: 10,
    dots: false,
    beforeChange: (_, next) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          centerMode: false,
          arrows: false,
          swipeToSlide: true,
          touchThreshold: 10,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          centerMode: false,
          arrows: false,
          swipeToSlide: true,
          touchThreshold: 10,
        },
      },
    ],
  };

  return (
    <div
      className="categories-list text-center d-flex align-items-center position-relative category-carousel-wrapper"
      style={{ paddingBottom: isMobile ? "36px" : "0" }}
    >
      {isMobile ? (
        <div
          className="category-slider-mobile"
          style={{ width: "100%", position: "relative", padding: "0 34px" }}
        >
          <Slider ref={sliderRef} {...settings}>
            <div>
              <button
                className={`btn category-btn ${localActiveIndex === -1 ? "active" : ""}`}
                onClick={handleResetClick}
                style={{
                  ...(localActiveIndex === -1 ? buttonStyles.active : buttonStyles.inactive),
                  fontSize: "0.8rem",
                  padding: "0.3rem 0.8rem",
                  whiteSpace: "nowrap",
                  borderRadius: "24px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  overflow: "hidden",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>All Kids</span>
                <span
                  style={{
                    borderRadius: "10px",
                    padding: "2px 6px",
                    fontSize: "0.8rem",
                    fontWeight: "bold",
                    background: "rgba(0, 0, 0, 0.1)",
                    flexShrink: 0,
                  }}
                >
                  {getCategoryCount(null)}
                </span>
              </button>
            </div>

            {sortedCategories.map((category, index) => (
              <div key={category.id}>
                <button
                  className={`btn category-btn ${index === localActiveIndex ? "active" : ""}`}
                  onClick={(e) => handleCategoryClick(e, category.id, index)}
                  style={{
                    ...(index === localActiveIndex ? buttonStyles.active : buttonStyles.inactive),
                    fontSize: "0.8rem",
                    padding: "0.3rem 0.8rem",
                    whiteSpace: "nowrap",
                    borderRadius: "24px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    overflow: "hidden",
                  }}
                >
                  <span className="category-label">{category.category_name || category.name}</span>
                  <span
                    style={{
                      background: index === localActiveIndex ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                      borderRadius: "8px",
                      padding: "1px 4px",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {getCategoryCount(category.id)}
                  </span>
                </button>
              </div>
            ))}
          </Slider>

          {showMobileArrows && (
            <>
              <button
                type="button"
                className="category-carousel-arrow category-carousel-arrow-prev"
                aria-label="Previous category"
                onClick={() => sliderRef.current?.slickPrev()}
              >
                <FaChevronLeft size={16} />
              </button>

              <button
                type="button"
                className="category-carousel-arrow category-carousel-arrow-next"
                aria-label="Next category"
                onClick={() => sliderRef.current?.slickNext()}
              >
                <FaChevronRight size={16} />
              </button>
            </>
          )}

          <div className="category-carousel-dots" role="tablist" aria-label="Category slider pagination">
            {Array.from({ length: totalSlides }).map((_, index) => {
              const isActive = currentSlide === index;
              return (
                <button
                  key={index}
                  type="button"
                  className={`category-dot ${isActive ? "active" : ""}`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={isActive ? "true" : "false"}
                  onClick={() => sliderRef.current?.slickGoTo(index)}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <>
          <button
            type="button"
            className="desktop-category-arrow desktop-category-arrow-prev"
            aria-label="Previous category"
            onClick={() => scrollDesktopCategories("left")}
          >
            <FaChevronLeft size={16} />
          </button>

          <div
            className="categories d-md-flex flex-nowrap align-items-center"
            ref={desktopCategoriesRef}
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "10px",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              scrollSnapType: "x mandatory",
              padding: "0",
              width: "100%",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <button
              className={`btn category-btn ${localActiveIndex === -1 ? "active" : ""}`}
              onClick={handleResetClick}
              style={{
                ...(localActiveIndex === -1 ? buttonStyles.active : buttonStyles.inactive),
                fontSize: "1rem",
                padding: "0.5rem 1.5rem",
                flexShrink: 0,
                whiteSpace: "nowrap",
                borderRadius: "24px",
                scrollSnapAlign: "start",
              }}
            >
              All Kids
              <span
                style={{
                  borderRadius: "10px",
                  padding: "2px 6px",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                  minWidth: "20px",
                  textAlign: "center",
                  lineHeight: "1.2",
                  marginLeft: "6px",
                  background: "rgba(0, 0, 0, 0.1)",
                }}
              >
                {getCategoryCount(null)}
              </span>
            </button>

            {sortedCategories.map((category, index) => (
              <button
                key={category.id}
                className={`btn category-btn ${index === localActiveIndex ? "active" : ""}`}
                onClick={(e) => handleCategoryClick(e, category.id, index)}
                style={{
                  ...(index === localActiveIndex ? buttonStyles.active : buttonStyles.inactive),
                  fontSize: "1rem",
                  padding: "0.5rem 1.5rem",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  borderRadius: "24px",
                  scrollSnapAlign: "start",
                }}
              >
                {category.category_name || category.name}
                <span
                  style={{
                    background: index === localActiveIndex ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
                    borderRadius: "8px",
                    padding: "1px 4px",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    minWidth: "16px",
                    textAlign: "center",
                    lineHeight: "1.1",
                    marginLeft: "6px",
                  }}
                >
                  {getCategoryCount(category.id)}
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="desktop-category-arrow desktop-category-arrow-next"
            aria-label="Next category"
            onClick={() => scrollDesktopCategories("right")}
          >
            <FaChevronRight size={16} />
          </button>
        </>
      )}

      <style>{`
        .category-carousel-wrapper .slick-list {
          overflow: visible;
          border-radius: 0;
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
        }

        .category-carousel-wrapper .category-slider-mobile .slick-list {
          overflow: visible;
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          border-radius: 0 !important;
        }

        .category-carousel-wrapper .slick-track {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        .category-carousel-wrapper .slick-slide {
          margin: 0 !important;
        }

        .category-carousel-wrapper .slick-slide > div {
          padding: 0 4px;
        }

        .category-carousel-wrapper .category-carousel-arrow {
          position: absolute;
          top: 26%;
          transform: translateY(-50%);
          z-index: 40;
          width: 34px;
          height: 34px;
          border: 1.5px solid #2aa89a;
          background: #ffffff !important;
          color: #2aa89a;
          display: none !important;
          align-items: center;
          justify-content: center;
          box-shadow: none !important;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 50%;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          padding: 0;
          line-height: 1;
        }

        .category-carousel-wrapper .category-carousel-arrow:hover {
          color: #228d81;
          background: #ffffff !important;
          border-color: #228d81;
        }

        .category-carousel-wrapper .category-carousel-arrow-prev {
          left: 0px;
        }

        .category-carousel-wrapper .category-carousel-arrow-next {
          right: 0px;
        }

        .category-carousel-wrapper .slick-prev,
        .category-carousel-wrapper .slick-next {
          display: flex !important;
          background: transparent !important;
          border: 0 !important;
          box-shadow: none !important;
          outline: none !important;
          z-index: 50 !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
        }

        .category-carousel-wrapper .slick-prev:after,
        .category-carousel-wrapper .slick-next:after {
          content: none !important;
          display: none !important;
          background: transparent !important;
          box-shadow: none !important;
        }

        .category-carousel-wrapper .category-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          flex: 1;
        }

        .category-carousel-wrapper .desktop-category-arrow {
          width: 34px;
          height: 34px;
          border: 1.5px solid #2aa89a;
          background: #ffffff;
          color: #2aa89a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: none;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 50%;
          padding: 0;
          line-height: 1;
          flex-shrink: 0;
        }

        .category-carousel-wrapper .desktop-category-arrow:hover {
          color: #228d81;
          border-color: #228d81;
        }

        .category-carousel-wrapper .desktop-category-arrow-prev {
          margin-right: 8px;
        }

        .category-carousel-wrapper .desktop-category-arrow-next {
          margin-left: 8px;
        }

        .category-carousel-wrapper .slick-prev:before,
        .category-carousel-wrapper .slick-next:before {
          content: "" !important;
        }

        .category-carousel-wrapper .category-carousel-dots {
          margin-top: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 12px;
          position: relative;
          z-index: 3;
        }

        .category-carousel-wrapper .category-dot {
          width: 8px;
          height: 8px;
          border: 0;
          border-radius: 999px;
          background: #b9c4c2;
          padding: 0;
          transition: all 0.2s ease;
          opacity: 1;
        }

        .category-carousel-wrapper .category-dot.active {
          width: 24px;
          background: #299d8e;
        }

        .category-carousel-wrapper .category-dot:focus-visible {
          outline: 2px solid #299d8e;
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .category-carousel-wrapper .desktop-category-arrow {
            display: none;
          }

          .category-carousel-wrapper .category-slider-mobile {
            padding: 0 34px !important;
          }

          .category-carousel-wrapper .category-carousel-arrow {
            display: flex !important;
            width: 34px;
            height: 34px;
            top: 26% !important;
          }

          .category-carousel-wrapper .category-carousel-arrow-prev {
            left: 0px;
          }

          .category-carousel-wrapper .category-carousel-arrow-next {
            right: 0px;
          }
        }
      `}</style>
    </div>
  );
};

export default Category;
